import type {
    Translatable,
    TranslatableValueFactoryParams,
} from "@@/shared/translations/translation";
import { exists } from "@@/utilities/exists";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { IsEqual } from "type-fest";
import { z } from "zod";
import {
    productFormFieldCheckboxFactory,
    productFormFieldCheckboxZodObject,
} from "../fields/checkbox-field/product-form-field.checkbox";
import {
    productFormFieldSelectFactory,
    productFormFieldSelectZodObject,
} from "../fields/select-field/product-form-field.select";
import type { ProductFormFieldId } from "./product-form-field-id";
import type { ProductFormFieldGroupableQuantity } from "./product-form-field-quantity";
import {
    productFormFieldTextFactory,
    productFormFieldTextZodObject,
} from "./text-field/product-form-field.text";

const productFormFieldZodUnion = z.union([
    productFormFieldCheckboxZodObject,
    productFormFieldTextZodObject,
    productFormFieldSelectZodObject,
]);

type ProductFormField = z.infer<typeof productFormFieldZodUnion>;

const productFormFieldFactory = {
    textBox: productFormFieldTextFactory,
    checkbox: productFormFieldCheckboxFactory,
    select: productFormFieldSelectFactory,
};

const productFormFieldTypeZodEnum = z.enum(["TEXT", "CHECKBOX", "SELECT"]);
type ProductFormFieldType = z.infer<typeof productFormFieldTypeZodEnum>;

assertTypeMatch<IsEqual<ProductFormFieldType, ProductFormField["type"]>>();

const productFormFieldTypeMeta: Record<
    ProductFormFieldType,
    {
        title: Translatable | TranslatableValueFactoryParams;
    }
> = {
    TEXT: {
        title: {
            sv: "Textfält",
            en: "Text field",
        },
    },
    CHECKBOX: {
        title: {
            sv: "Kryssruta",
            en: "Checkbox",
        },
    },
    SELECT: {
        title: {
            sv: "Flerval",
            en: "Select",
        },
    },
};

const productFormFieldUtils = {
    /**
     * Groups fields by their quantity (unless the quantity is a regular static number).
     */
    group: (
        productFormFields: ProductFormField[],
    ): (ProductFormField | ProductFormField[])[] => {
        {
            const mappedFields = new Map<ProductFormFieldId, ProductFormField>(
                productFormFields.map(field => [field._id, field]),
            );
            const sortOrder = productFormFields.map(field => field._id);
            const finished = new Set<
                ProductFormFieldGroupableQuantity | "__NO_GROUP"
            >();
            const groupedOrder: (
                | ProductFormFieldGroupableQuantity
                | "__NO_GROUP"
            )[] = [];
            const grouped = new Map<
                ProductFormFieldGroupableQuantity | "__NO_GROUP",
                ProductFormField[]
            >();
            grouped.set(
                "__NO_GROUP",
                productFormFields.filter(
                    field => typeof field.quantity === "number",
                ),
            );

            for (const fieldId of sortOrder) {
                const field = mappedFields.get(fieldId);
                if (!field) continue;
                if (typeof field.quantity === "number") continue;
                if (finished.has(field.quantity)) continue;
                finished.add(field.quantity);

                const matchingFields = productFormFields
                    // only include fields that have the same quantity as the field
                    .filter(item => item.quantity === field.quantity)
                    // sort fields by field._id by the order they appear in the sortOrder
                    .sort((a, b) => {
                        const aIndex = sortOrder.indexOf(a._id);
                        const bIndex = sortOrder.indexOf(b._id);
                        return aIndex - bIndex;
                    });

                groupedOrder.push(field.quantity);
                grouped.set(field.quantity, matchingFields);
            }

            // eslint-disable-next-line no-console
            console.log("GROUPS", { sortOrder, groupedOrder, grouped });

            const result: (ProductFormField | ProductFormField[])[] =
                groupedOrder
                    .filter(groupedBy => groupedBy !== "__NO_GROUP")
                    .map(groupedBy => grouped.get(groupedBy))
                    .filter(exists);

            const noGroup = grouped.get("__NO_GROUP");
            if (noGroup?.length) {
                result.push(...noGroup);
            }

            return result;
        }
    },
};

export {
    productFormFieldFactory,
    productFormFieldTypeMeta,
    productFormFieldTypeZodEnum,
    productFormFieldUtils,
    productFormFieldZodUnion,
};
export type { ProductFormField, ProductFormFieldType };
